import React, { useEffect, useState } from 'react'
import { Drawer, Button, Form, Input } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faKey, faLock } from '@fortawesome/pro-solid-svg-icons'
import {
  mapStateToProps,
  mapDispatchToProps,
  connect
} from '../../../reducers/Dispatchers'
import { getIdOrNull } from '../../../utils'
import { changeUserPassword } from '../../../utils/api/user'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { HttpStatuses } from '../../../utils/http'
import { getPasswordPolicyError } from '../../../utils/validators'
import '../../../assets/admin-reset-password.scss'

const FormItem = Form.Item

/**
 * The scope is checked again target by target on the server, so nothing can be fully pre-validated:
 * the returned status gets translated. 403 covers both "outside your organization" and "locked
 * profile", 404 a profile without account or deleted, 409 an account managed by an external SSO.
 */
const ERROR_MESSAGES = Object.freeze({
  [HttpStatuses.BAD_REQUEST]: 'password_policy.rules',
  [HttpStatuses.FORBIDDEN]: 'admin_reset_password.error.forbidden',
  [HttpStatuses.NOT_FOUND]: 'admin_reset_password.error.not_found',
  [HttpStatuses.CONFLICT]: 'admin_reset_password.error.external'
})

/**
 * Password reset by an organization admin (school or institution): the password is set directly,
 * with no email and no action from the user, through POST /api/users/{profile}/change-password.
 *
 * `userToResetPassword` is either a user — which IS the target profile — or a student, whose profile
 * is carried by its `user` property (null as long as they have no account).
 *
 * The password belongs to the account, not to the profile: when the user owns a profile in another
 * organization, their login changes there too. The admin is warned about it in the drawer.
 */
const AdminResetPassword = props => {
  const [loading, setLoading] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const target = props.userToResetPassword
  const profileId = target === null ? null : getIdOrNull(target.user !== undefined ? target.user : target)
  const email = target === null ? null : (target.user?.email ?? target.email ?? null)

  // The drawer stays mounted between two openings: start from a blank form for every target.
  useEffect(() => {
    setPassword('')
    setPasswordConfirm('')
    setSubmitted(false)
  }, [profileId])

  const policyError = getPasswordPolicyError(password)
  const passwordError = submitted && policyError !== null ? props.t(policyError) : null
  const passwordConfirmError = submitted && passwordConfirm !== password ? props.t('common.passwords_mismatch') : null

  const handleDrawerClose = () => {
    if (typeof props.onClose === 'function') {
      props.onClose()
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    setSubmitted(true)

    if (policyError !== null || password !== passwordConfirm) {
      return
    }

    setLoading(true)

    changeUserPassword(props.getUser, profileId, password).then(() => {
      handleDrawerClose()
      onSuccess(props.t('admin_reset_password.success'))
    }).catch(error => {
      onError(props.t(ERROR_MESSAGES[error?.status] ?? 'common.error'))
    }).finally(() => setLoading(false))
  }

  return (
    <Drawer
      title={props.t('admin_reset_password.title')}
      width='30%'
      onClose={handleDrawerClose}
      visible={target !== null}
    >
      <div className='admin-reset-password-wrapper'>
        <div className='reset-password-description'>
          {props.t('admin_reset_password.description')}
          {email !== null && (
            <div><b>{email}</b></div>
          )}
        </div>
        {profileId === null
          ? (
            <div className='reset-password-warning'>
              {props.t('admin_reset_password.no_account')}
            </div>
          )
          : (
            <Form onSubmit={handleSubmit} className='full-height flex-column'>
              <div className='reset-password-policy'>
                {props.t('password_policy.rules')}
              </div>
              <FormItem validateStatus={passwordError ? 'error' : ''} help={passwordError}>
                <Input
                  prefix={<FontAwesomeIcon icon={faLock} />}
                  type='password'
                  placeholder={props.t('common.new_password')}
                  value={password}
                  autoComplete='new-password'
                  onChange={e => setPassword(e.target.value)}
                />
              </FormItem>
              <FormItem validateStatus={passwordConfirmError ? 'error' : ''} help={passwordConfirmError}>
                <Input
                  prefix={<FontAwesomeIcon icon={faLock} />}
                  type='password'
                  placeholder={props.t('common.confirm_new_password')}
                  value={passwordConfirm}
                  autoComplete='new-password'
                  onChange={e => setPasswordConfirm(e.target.value)}
                />
              </FormItem>
              <div className='reset-password-warning'>
                {props.t('admin_reset_password.account_warning')}
              </div>
              <Button type='primary' htmlType='submit' className='full-width' loading={loading}>
                <FontAwesomeIcon icon={faKey} />&nbsp;
                {props.t('admin_reset_password.submit')}
              </Button>
            </Form>
          )}
      </div>
    </Drawer>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminResetPassword)
